import { ApiClient } from '@listening/api-contract/api-client-tag';
import {
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type { Context, Effect } from 'effect';
import { type Draft, produce } from 'immer';
import { useMemo } from 'react';
import { useOptimisticLifecycle } from './optimistic-list-entity-lifecycle';
import { useEq } from '../runtime-context';

// Manual, collection-shaped optimistic mutations over a single TanStack Query
// list cache — the interim replacement for TanStack DB collections while DB
// SSR (TanStack/db#709) is unavailable. The surface mirrors a DB collection
// (`.get` / `.update` / `.delete` / `.insert` / `.utils.refetch`, reads via a
// `useList(select)` hook) so the switch back is mechanical: delete this file
// and re-point consumers at `createXCollection(...)`.
//
// Lifecycle is provided by `useOptimisticLifecycle` (canonical TanStack
// Query pattern: onMutate awaits cancelQueries → snapshots → writes
// optimistic state; onError restores; onSettled invalidates). Each callback
// is an Effect program run via `runtime.runPromise(...)`, runtime resolved
// from `AppRuntimeContext`.
//
// See docs/projects/complete/web/query-stack-route-loaders.md and
// docs/projects/complete/web/shared-tanstack-query.md.

type ApiClientCtx = Context.Service.Identifier<typeof ApiClient>;
// The error channel is left broad (`{ _tag: string }`) rather than per-program:
// each entity's list/update/delete programs declare different `.addError`s, and
// the write path doesn't `.match` on tags (the helper owns rollback). Failures
// still surface as `EffectQueryFailure` for callsite `onError`. Typed-tag
// matching belongs on reads + standalone mutations, not list-entity writes.
type Eff<A> = Effect.Effect<A, { _tag: string }, ApiClientCtx>;

// Transaction-shaped result so existing `await tx.isPersisted.promise` callsites
// are unchanged (DB's `collection.update(...)` returns a Transaction).
export type TxLike = { isPersisted: { promise: Promise<void> } };

// Per-entity persistence config. Each op MUST return an `Eff<unknown>`. A
// cache-only optimistic flip — apply a row change but skip the server call —
// is not expressible here: there's no rollback owner, so a failed flow leaves
// the cache permanently wrong. Cache-only writes belong in a hand-rolled
// `useMutation` whose `onError` restores its own snapshot.
type Operations<Row> = {
  update?: (args: {
    id: string;
    prev: Row;
    next: Row;
    metadata?: unknown;
  }) => Eff<unknown>;
  // `prev` is the cached row when present, but is OPTIONAL: a delete may be
  // issued for a row that isn't in the local list cache (e.g. a deep-linked
  // detail view whose list was never loaded). The DELETE must still fire, so
  // we never gate the request on having `prev`. None of the configured remove
  // ops read it today.
  remove?: (args: { id: string; prev?: Row }) => Eff<unknown>;
  insert?: (args: { row: Row }) => Eff<unknown>;
};

export type EntityConfig<Row> = {
  queryKey: readonly unknown[];
  listEffect: () => Eff<ReadonlyArray<Row>>;
  getId: (row: Row) => string;
  staleTime?: number;
  // Background poll cadence (ms) for the list read. Set this for entities that
  // change out-of-band — e.g. audio items, where a conversion finishes
  // server-side or another client (mobile, browser-ext) uploads, and the web
  // app must surface it without a manual refresh. TanStack Query pauses the
  // interval while the tab is backgrounded (`refetchIntervalInBackground`
  // defaults false), so this only polls foreground tabs. Omit for entities
  // that only change via this client's own mutations (which already invalidate).
  refetchInterval?: number;
  operations: Operations<Row>;
};

type UpdateRecipe<Row> = (draft: Draft<Row>) => void;

export type ListEntity<Row> = {
  get: (id: string) => Row | undefined;
  update: (
    id: string,
    arg1: UpdateRecipe<Row> | { metadata?: unknown },
    arg2?: UpdateRecipe<Row>,
  ) => TxLike;
  delete: (id: string) => TxLike;
  insert: (row: Row) => TxLike;
  utils: { refetch: () => Promise<unknown> };
};

const resolvedTx = (): TxLike => ({
  isPersisted: { promise: Promise.resolve() },
});

export function createListEntity<Row>(config: EntityConfig<Row>) {
  const {
    queryKey,
    listEffect,
    getId,
    staleTime,
    refetchInterval,
    operations,
  } = config;

  // Suspense read — for loader-backed routes (≙ `useLiveSuspenseQuery`). Views
  // derive via `select`. Exposed as a sibling hook (can't live on the
  // useEntity() object — hook rules). Resolves warm when the route loader
  // prefetched `queryKey`; suspends to the nearest boundary otherwise.
  function useList<S = ReadonlyArray<Row>>(
    select?: (rows: ReadonlyArray<Row>) => S,
  ): S {
    const eq = useEq();
    const query = useSuspenseQuery({
      ...eq.queryOptions({
        queryKey,
        queryFn: () => listEffect(),
        staleTime,
        refetchInterval,
      }),
      select: select ? (rows: ReadonlyArray<Row>) => select(rows) : undefined,
    });
    return query.data;
  }

  // Non-suspense read — for ambient UI / dialogs that may render off a
  // loader-backed route (≙ `useLiveQuery`). Returns the full query result so
  // callers can read `.data` / `.isLoading`; fetches if the cache is cold.
  // `enabled: false` skips the fetch entirely (used by SSR-reachable consumers
  // on public routes — e.g. PlayBar's continue-listening list, which must not
  // fire the authed listEffect on `/doc/$docId`).
  function useListQuery<S = ReadonlyArray<Row>>(
    select?: (rows: ReadonlyArray<Row>) => S,
    options?: { enabled?: boolean },
  ): UseQueryResult<S> {
    const eq = useEq();
    return useQuery({
      ...eq.queryOptions({
        queryKey,
        queryFn: () => listEffect(),
        staleTime,
        refetchInterval,
      }),
      enabled: options?.enabled ?? true,
      select: select ? (rows: ReadonlyArray<Row>) => select(rows) : undefined,
    }) as UseQueryResult<S>;
  }

  function useEntity(): ListEntity<Row> {
    const qc = useQueryClient();
    const eq = useEq();

    // Per-op `useMutation`s with full TanStack lifecycle. The persistence
    // Effect arrives in `variables.effect`; `eq.mutationOptions` runs it
    // through the managed runtime and wraps the failure as
    // `EffectQueryFailure` for callsite `error.match`. Cache work
    // (cancel/snapshot/optimistic write/rollback/invalidate) lives in
    // `useOptimisticLifecycle`, also runtime-driven.
    const updateLifecycle = useOptimisticLifecycle<
      Row,
      { id: string; next: Row }
    >({
      queryKey,
      optimistic: (rows, v) =>
        rows.map((r) => (getId(r) === v.id ? v.next : r)),
    });
    const updateMutation = useMutation({
      ...eq.mutationOptions({
        mutationKey: [...queryKey, 'update'],
        mutationFn: (v: { effect: Eff<unknown>; id: string; next: Row }) =>
          v.effect,
      }),
      ...updateLifecycle,
    });

    const deleteLifecycle = useOptimisticLifecycle<Row, { id: string }>({
      queryKey,
      optimistic: (rows, v) => rows.filter((r) => getId(r) !== v.id),
    });
    const deleteMutation = useMutation({
      ...eq.mutationOptions({
        mutationKey: [...queryKey, 'delete'],
        mutationFn: (v: { effect: Eff<unknown>; id: string }) => v.effect,
      }),
      ...deleteLifecycle,
    });

    const insertLifecycle = useOptimisticLifecycle<Row, { row: Row }>({
      queryKey,
      optimistic: (rows, v) => [...rows, v.row],
    });
    const insertMutation = useMutation({
      ...eq.mutationOptions({
        mutationKey: [...queryKey, 'insert'],
        mutationFn: (v: { effect: Eff<unknown>; row: Row }) => v.effect,
      }),
      ...insertLifecycle,
    });

    const updateAsync = updateMutation.mutateAsync;
    const deleteAsync = deleteMutation.mutateAsync;
    const insertAsync = insertMutation.mutateAsync;

    return useMemo<ListEntity<Row>>(() => {
      const read = (): ReadonlyArray<Row> =>
        qc.getQueryData<ReadonlyArray<Row>>(queryKey) ?? [];

      const get = (id: string) => read().find((r) => getId(r) === id);

      const refetch = () => qc.invalidateQueries({ queryKey });

      const update: ListEntity<Row>['update'] = (id, arg1, arg2) => {
        const recipe = typeof arg1 === 'function' ? arg1 : arg2;
        const metadata = typeof arg1 === 'function' ? undefined : arg1.metadata;
        const operation = operations.update;
        // Fail closed: calling update on an entity with no `update` op is a
        // wiring bug. Silently no-op'ing would hide it. (Cache-only flips
        // belong in a hand-rolled useMutation — no rollback owner here.)
        if (!operation) {
          throw new Error(
            `optimistic-list-entity[${String(queryKey)}]: update() called but no \`update\` operation is configured.`,
          );
        }
        const prev = get(id);
        // Fail closed on a cache miss. We can't build the optimistic `next`
        // (or the prev→next diff the op PATCHes) without `prev`, and silently
        // resolving would report success with no server write. A real DB
        // collection throws on an unknown key — match that instead of lying.
        if (!prev) {
          throw new Error(
            `optimistic-list-entity[${String(queryKey)}]: update(${id}) — row not in the list cache. Load the list before mutating, or hand-roll the mutation.`,
          );
        }
        // No recipe → no row change requested → nothing to persist.
        if (!recipe) return resolvedTx();
        const next = produce(prev, recipe);
        const effect = operation({ id, prev, next, metadata });
        const promise = updateAsync({ effect, id, next }).then(() => undefined);
        return { isPersisted: { promise } };
      };

      const del: ListEntity<Row>['delete'] = (id) => {
        const operation = operations.remove;
        if (!operation) {
          throw new Error(
            `optimistic-list-entity[${String(queryKey)}]: delete() called but no \`remove\` operation is configured.`,
          );
        }
        // Fall through to the server even on a cache miss — the remove op only
        // needs the id, so a missing `prev` must NOT short-circuit the DELETE.
        // The optimistic filter removes nothing when the row is absent; the
        // server delete + invalidate still run.
        const prev = get(id);
        const effect = operation({ id, prev });
        const promise = deleteAsync({ effect, id }).then(() => undefined);
        return { isPersisted: { promise } };
      };

      const insert: ListEntity<Row>['insert'] = (row) => {
        const operation = operations.insert;
        if (!operation) {
          throw new Error(
            `optimistic-list-entity[${String(queryKey)}]: insert() called but no \`insert\` operation is configured.`,
          );
        }
        const effect = operation({ row });
        const promise = insertAsync({ effect, row }).then(() => undefined);
        return { isPersisted: { promise } };
      };

      return { get, update, delete: del, insert, utils: { refetch } };
    }, [qc, updateAsync, deleteAsync, insertAsync]);
  }

  return { useEntity, useList, useListQuery };
}
